import { Spine, TopNavigationItem } from '@wk/components-react16';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  selectSidebarNavigated,
  setSidebarNavigated,
} from 'redux/modal/ModalSlice';
import CsbErrorBoundary from 'components/CsbErrorBoudary/CsbErrorBoundary';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  NOTIFICATION_BOX_MESSAGES,
  SPINE_MENU_ITEMS,
  USER_PERMISSIONS,
} from 'utils/common-constants';
import style from './SpineMenuCsb.module.scss';
import { selectUserPermissions } from 'redux/auth/AuthSlice';
import { hasPermission } from 'utils/commonFunctions/CommonFunctions';
import AuthErrorNotification from 'components/AuthErrorNotification/AuthErrorNotification';
import { usePostLogoutMutation } from 'api/auth/authApi';

const MAX_WIDTH = 450;

const SpineMenuCsb = memo((props: any) => {
  const userPermission = useAppSelector(selectUserPermissions);
  const [logout] = usePostLogoutMutation();

  const viewOrchestrationItem = {
    id: SPINE_MENU_ITEMS.VIEW_ORCHESTRATION,
    text: 'Orchestration Status',
    icon: 'list-bullet-check',
    href: '/dashboard',
    permission: [USER_PERMISSIONS.ORCHESTRATIONS_RUN_HISTORY_PAGE],
  };
  const runOrchestrationItem = {
    id: SPINE_MENU_ITEMS.RUN_ORCHESTRATION,
    text: 'Run Orchestration',
    icon: 'play',
    href: '/run-orchestrations',
    permission: [USER_PERMISSIONS.ORCHESTRATION_RUN_PAGE],
  };
  const editOrchestrationItem = {
    id: SPINE_MENU_ITEMS.EDIT_ORCHESTRATION,
    text: 'Design Orchestration',
    icon: 'pencil',
    href: '/design-orchestration',
    permission: [USER_PERMISSIONS.DESIGNER_PAGE],
  };
  const manageModulesItem = {
    id: SPINE_MENU_ITEMS.MANAGE_MODULES,
    text: 'Manage Modules',
    icon: 'cubes',
    href: '/manage-modules',
    permission: [USER_PERMISSIONS.MODULES_PAGE],
  };

  const closedItems = [
    viewOrchestrationItem,
    runOrchestrationItem,
    editOrchestrationItem,
    manageModulesItem,
  ].filter((item) => hasPermission(userPermission, item.permission));

  const openedItems = [
    { ...viewOrchestrationItem, text: 'Orchestration Status' },
    { ...runOrchestrationItem, text: 'Run Orchestration' },
    { ...editOrchestrationItem, text: 'Design Orchestration' },
    { ...manageModulesItem, text: 'Manage Modules' },
  ].filter((item) => hasPermission(userPermission, item.permission));

  const [items, setItems] = useState<TopNavigationItem[]>(closedItems);
  const [width, setWidth] = useState(272);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState(() => {
    if (window.location.pathname === '/stack-trace') {
      return '-1';
    }
    // Ensure we have a valid selectedItem that exists in the items
    const firstAvailableItem = closedItems[0];
    return firstAvailableItem
      ? firstAvailableItem.id
      : SPINE_MENU_ITEMS.VIEW_ORCHESTRATION;
  });
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const navigationFromModal = useAppSelector(selectSidebarNavigated);
  const onlyVisualSelection = useRef(false);
  const dispatch = useAppDispatch();
  const addedListeners = new Set();
  const [tooltipElements, setTooltipElements] = useState<NodeListOf<Element>>();
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const onUserRequest = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleDefaultOnUserRequest = () => {
    if (window.innerWidth < 1024) {
      onUserRequest();
    }
  };

  const onSelect = (item: TopNavigationItem) => {
    setSelectedItem(item.id);
  };

  useEffect(() => {
    if (onlyVisualSelection.current === false) {
      if (selectedItem === SPINE_MENU_ITEMS.VIEW_ORCHESTRATION) {
        navigate('/dashboard');
      } else if (selectedItem === SPINE_MENU_ITEMS.RUN_ORCHESTRATION) {
        navigate('/run-orchestrations');
      } else if (selectedItem === SPINE_MENU_ITEMS.MANAGE_MODULES) {
        navigate('/manage-modules');
      } else if (selectedItem === SPINE_MENU_ITEMS.EDIT_ORCHESTRATION) {
        navigate('/design-orchestration');
      }
    } else {
      onlyVisualSelection.current = false;
    }
  }, [selectedItem]);

  useEffect(() => {
    if (navigationFromModal !== undefined) {
      onlyVisualSelection.current = true;
      dispatch(setSidebarNavigated(undefined));
      setSelectedItem(navigationFromModal);
    }
  }, [navigationFromModal]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const selectedItemFromPath = getItemFromPath(currentPath);

    if (selectedItemFromPath && selectedItemFromPath !== selectedItem) {
      setSelectedItem(selectedItemFromPath);
      onlyVisualSelection.current = true;
    }
  }, [window.location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getItemFromPath = (path: string): string | undefined => {
    if (path.includes('/dashboard')) {
      return SPINE_MENU_ITEMS.VIEW_ORCHESTRATION;
    } else if (path.includes('/run-orchestrations')) {
      return SPINE_MENU_ITEMS.RUN_ORCHESTRATION;
    }
    return undefined;
  };

  const onExpand = (item: TopNavigationItem) => {
    const currentlyExpanded: string[] = [...expandedItems];
    if (currentlyExpanded.includes(item.id)) {
      const newExpanded = currentlyExpanded.filter(
        (expandedItem) => expandedItem !== item.id
      );
      setExpandedItems(newExpanded);
    } else {
      const newExpanded = currentlyExpanded.concat(item.id);
      setExpandedItems(newExpanded);
    }
  };

  const onWidthChange = (newWidth: number) => {
    newWidth > MAX_WIDTH ? setWidth(MAX_WIDTH) : setWidth(newWidth);
  };

  const onOpenBegin = () => {
    setItems(openedItems);
  };

  const onCloseEnd = () => {
    setItems(closedItems);
  };

  useEffect(() => {
    setTooltipElements(document.querySelectorAll('li [data-e2e="cg-tooltip"]'));
  }, []);

  useEffect(() => {
    tooltipElements?.forEach((element) => {
      let timer: NodeJS.Timeout;
      const parentElement = element?.parentElement?.firstChild;

      if (!parentElement || addedListeners.has(parentElement)) {
        return;
      }

      const handleMouseLeave = () => {
        if (!element.classList.contains('cg-tooltip-hide')) {
          element.classList.add('cg-element-closing');
          timer = setTimeout(() => {
            (element as HTMLElement).style.visibility = 'hidden';
          }, 300);
        }
      };

      const handleMouseEnter = () => {
        (element as HTMLElement).style.visibility = 'visible';
        clearTimeout(timer);
        element.classList.add('cg-tooltip-open');
        element.classList.remove('cg-element-closing');
      };

      parentElement.addEventListener('mouseleave', handleMouseLeave);
      parentElement.addEventListener('mouseenter', handleMouseEnter);

      addedListeners.add(parentElement);

      return () => {
        parentElement.removeEventListener('mouseleave', handleMouseLeave);
        parentElement.removeEventListener('mouseenter', handleMouseEnter);
        addedListeners.delete(parentElement);
      };
    });
  }, [tooltipElements]);

  // Necessary to avoid the default behavior of Spine expand click
  // TODO: Check to update to @wk/components-v3-react16 Spine when stable
  useEffect(() => {
    const element = document.querySelector('.cg-spine-aside-footer');
    element?.addEventListener('click', onUserRequest);

    return () => {
      element?.removeEventListener('click', onUserRequest);
    };
  }, [onUserRequest, windowSize]);

  return (
    <CsbErrorBoundary>
      <div className={style['spine-menu']} data-testid="SpineMenuCsb">
        {items.length > 0 && (
          <Spine
            navigationItems={items}
            enableHoverOpen={false}
            controlMode="controlled"
            expandedItems={expandedItems}
            selectedItem={selectedItem}
            isOpen={isOpen}
            widthControlMode="controlled"
            width={width}
            onExpandClick={(event: any) => onExpand(event)}
            onItemClick={(event: any) => onSelect(event)}
            onOpenBegin={() => onOpenBegin()}
            onCloseEnd={() => onCloseEnd()}
            onWidthChange={(event: any) => onWidthChange(event)}
            onUserRequest={handleDefaultOnUserRequest}
            isClassicStyle={false}
          >
            {props.children}
          </Spine>
        )}
        {items.length === 0 && (
          <div>
            <AuthErrorNotification
              authError={NOTIFICATION_BOX_MESSAGES.PERMISSION_ERROR}
              isLoading={false}
              handleLogout={() => logout(null)}
            />
          </div>
        )}
      </div>
    </CsbErrorBoundary>
  );
});

export default SpineMenuCsb;
